import { PartitionOutlined } from '@ant-design/icons';
import { UnicornDetectorIcon } from '@mm/icons/AntDesignIcons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import WidgetModel, { NavigateConfig } from '@mm/state/model/WidgetModel';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getUniqId } from '@mm/utils';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { FilterBarItem } from '../../../../../Audiences/FilterBar/FilterBarItem';
import { useCreateNewAudience } from '../../../../../Audiences/use-create-new-audience';
import { WithAdderButton } from '../Adder/Adder';
import css from './UnicornDetector.module.css';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';

type Props = {
  className?: string;
  isUnicornPath?: boolean;
} & NodeProps<{ widget: WidgetModel; pageGroupId?: number }>;

export const ConditionIcon = PartitionOutlined;

const noop = () => {};

export const UnicornDetector: React.FC<Props> = ({
  className,
  data,
  isDragging,
  isUnicornPath,
  children,
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget.config as NavigateConfig;

  const { audienceId } = config;
  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId || null),
  );

  const hasNoAudience = !config.audienceId;

  const viewActions = useViewActions();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const { createAudienceAndOpenMenu } = useCreateNewAudience();
  const audiencesCRUDActions = useAudiencesCRUDActions();

  const onClose = (audienceId: number) => {
    if (!widget.config.audienceId && audienceId) {
      try {
        commonWidgetHandlers.update({
          ...widget,
          config: {
            ...config,
            audienceId,
          } as any,
        });
      } catch (err) {}
    }
  };

  const onClick = async () => {
    console.debug(`Condition: onClick: `, { data, widget });
    const props = {
      audienceId: config.audienceId,
      onClose,
      createBtnTitle: 'Save',
      title: 'Unicorn Detector',
    };

    if (isDragging) {
      return;
    }

    if (hasNoAudience) {
      const newAudienceId = await createAudienceAndOpenMenu({
        withRedirectOnClose: false,
        hiddenAudience: true,
      });
      if (newAudienceId) {
        props.audienceId = newAudienceId;
        viewActions.showSlideoutMenu('newAudience', props);
      }
      return;
    }

    viewActions.showSlideoutMenu('editAudience', props);
  };

  const [filters, setFilters] = useState<
    React.ComponentProps<typeof FilterBarItem>['filter'][]
  >([]);

  useOpenOnReady(widget, onClick);

  useEffect(() => {
    console.debug('Condition: useEffect');

    (async () => {
      if (audienceId) {
        await audiencesCRUDActions.retrieveAudienceById(chatbotId, audienceId);
      }
    })();
  }, []);

  useEffect(() => {
    setFilters([
      ...Object.entries(audience?.filterParams || {})
        .filter(([k, _v]) => k !== 'logic')
        .map(([_k, v]) => ({
          ...v,
          owner: 'filterParams',
          dirty: false,
          uniqId: getUniqId('filterParams'),
        })),

      ...Object.values(audience?.tagsFilters || {}).map(f => ({
        ...f,
        owner: 'tagsFilters',
        dirty: false,
        uniqId: getUniqId('tagsFilters'),
      })),
    ]);
  }, [JSON.stringify(audience)]);

  const countedFiltersByType = useMemo(() => {
    const groupedByType = (filters || []).reduce((acc, cur) => {
      acc[cur.filterType] = (acc[cur.filterType] || 0) + 1;
      return acc;
    }, {});

    if (groupedByType['variable_values']) {
      groupedByType['attributes'] = groupedByType['variable_values'];
      delete groupedByType['variable_values'];
    }

    if (groupedByType['tags_filters']) {
      groupedByType['tags'] = groupedByType['tags_filters'];
      delete groupedByType['tags_filters'];
    }

    if (groupedByType['eu_limits']) {
      groupedByType['EU limits'] = groupedByType['eu_limits'];
      delete groupedByType['eu_limits'];
    }

    const result = Object.entries(groupedByType).map(([type, count], idx) => {
      const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
      return `${capitalizedType}: ${count}`;
    });

    return result.join(', ');
  }, [filters]);

  return (
    <>
      <div className={classNames(className, css.container)} onClick={onClick}>
        <Handle type="target" position={Position.Top} />

        <div className={css.body}>
          <div className={css.iconContainer}>
            <div className={css.icon}>
              <UnicornDetectorIcon size={148} />
            </div>
          </div>
          <div className={css.contentContainer}>
            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <div className={css.header}>
              <span className={css.placeholder}>UNICORN DETECTOR</span>
            </div>

            <div className={css.conditions}>
              {!filters.length && (
                <span className={css.condition}>
                  <span className={css.filter}>Please add condition</span>
                </span>
              )}

              {filters.length < 4 ? (
                filters.map((filter, i, a) => (
                  <div key={i}>
                    <div className={css.condition}>
                      <FilterBarItem
                        className={css.filter}
                        filter={filter}
                        onStartEdit={noop}
                        onCancelEdit={noop}
                        onUpdate={noop}
                        editing={false}
                      />
                    </div>
                    {/*{a.length - 1 !== i && <>&nbsp;and&nbsp;</>}*/}
                  </div>
                ))
              ) : (
                <div className={css.conditionsCollapsed}>
                  {countedFiltersByType}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          width: '200px',
          margin: '0 auto',
        }}
      >
        <WithAdderButton
          pageId={data.widget.config.pageId}
          sortOrder={0}
          pageGroupId={data.pageGroupId}
          leadType="unicorn"
          widgetId={data.widget.id}
        >
          <Handle id="yes" type="source" position={Position.Bottom} />
        </WithAdderButton>

        <WithAdderButton
          pageId={data.widget.config.oppositePageId}
          sortOrder={0}
          pageGroupId={data.pageGroupId}
          leadType="donkey"
          widgetId={data.widget.id}
        >
          <Handle id="no" type="source" position={Position.Bottom} />
        </WithAdderButton>
      </div>
    </>
  );
};
